import { FileText } from 'lucide-react';

interface RecommendationsTabProps {
  keywords: any[];
  pageKeywords: any[];
  loading: boolean;
  error: string | null;
}

export function RecommendationsTab({ keywords, pageKeywords, loading, error }: RecommendationsTabProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">{error}</div>
    );
  }

  if (!keywords.length && !pageKeywords.length) {
    return <p className="text-gray-500">No recommendations available</p>;
  }

  const recommendations = [];

  const missingKeywords = keywords.filter(k => k.status === 'Missing');
  if (missingKeywords.length > 0) {
    recommendations.push(
      <div key="missing" className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Missing Keywords</h4>
        <p className="text-sm text-gray-600">
          Consider adding these important keywords to your content:
        </p>
        <ul className="mt-2 space-y-1">
          {missingKeywords.slice(0, 10).map((kw, i) => (
            <li key={i} className="text-sm text-red-600">• {kw.keyword}</li>
          ))}
          {missingKeywords.length > 10 && (
            <li className="text-sm text-gray-500">• and {missingKeywords.length - 10} more...</li>
          )}
        </ul>
      </div>
    );
  }

  const lowDensityKeywords = keywords.filter(k => k.status === 'Present' && k.densityRatio < 0.7);
  if (lowDensityKeywords.length > 0) {
    recommendations.push(
      <div key="low-density" className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Low Density Keywords</h4>
        <p className="text-sm text-gray-600">
          These keywords could be used more frequently:
        </p>
        <ul className="mt-2 space-y-1">
          {lowDensityKeywords.slice(0, 10).map((kw, i) => (
            <li key={i} className="text-sm text-yellow-600">
              • {kw.keyword} (density: {kw.density.toFixed(2)}% / avg: {kw.avgDensity.toFixed(2)}%)
            </li>
          ))}
          {lowDensityKeywords.length > 10 && (
            <li className="text-sm text-gray-500">• and {lowDensityKeywords.length - 10} more...</li>
          )}
        </ul>
      </div>
    );
  }

  if (pageKeywords.length > 0) {
    recommendations.push(
      <div key="page-keywords" className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">SEO Keywords</h4>
        <p className="text-sm text-gray-600">
          Make sure to include these target keywords:
        </p>
        <ul className="mt-2 space-y-1">
          {pageKeywords.slice(0, 10).map((kw, i) => (
            <li key={i} className="text-sm text-blue-600">• {kw.keyword}</li>
          ))}
          {pageKeywords.length > 10 && (
            <li className="text-sm text-gray-500">• and {pageKeywords.length - 10} more...</li>
          )}
        </ul>
      </div>
    );
  }

  return recommendations.length > 0 ? (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-900">Content Recommendations</h3>
      {recommendations}
    </div>
  ) : (
    <p className="text-green-600">Your content looks good! All important keywords are present with good density.</p>
  );
}